import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateMqttSource } from "@/api/reseller-manager/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getDrfFieldErrors } from "@/utils/errors";

interface CreateSourceDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function CreateSourceDialog({
	open,
	onOpenChange,
}: CreateSourceDialogProps) {
	const { t } = useTranslation();
	const [sn, setSn] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const create = useCreateMqttSource();

	useEffect(() => {
		if (open) {
			setSn("");
			setFieldErrors({});
		}
	}, [open]);

	function handleSubmit() {
		if (!sn.trim()) return;
		setFieldErrors({});
		create.mutate(
			{ sn: sn.trim() },
			{
				onSuccess: () => {
					toast.success(t("createSource.success"));
					onOpenChange(false);
				},
				onError: (error) => {
					setFieldErrors(getDrfFieldErrors(error));
					toast.error(t("createSource.error"));
				},
			},
		);
	}

	const canSubmit = sn.trim() && !create.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("createSource.title")}</DialogTitle>
					<DialogDescription>{t("createSource.description")}</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="source-name">
							{t("createSource.serialNumber")}
						</Label>
						<Input
							id="source-name"
							name="source-name"
							autoComplete="off"
							value={sn}
							onChange={(e) => setSn(e.target.value)}
							placeholder={t("createSource.placeholder")}
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
							aria-invalid={!!fieldErrors.sn}
						/>
						{fieldErrors.sn && (
							<p className="text-destructive text-sm" role="alert">
								{fieldErrors.sn}
							</p>
						)}
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={create.isPending}
					>
						{t("createSource.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{create.isPending
							? t("createSource.creating")
							: t("createSource.create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
